'use client';

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  MAPPED_TRANSLATION,
  VoucherType,
  public_env,
  formatPriceFromMinorUnit,
  parsePriceToMinorUnit,
  formatMinorUnitToRawPrice,
  getCurrencySymbol,
  hasBookableDuration,
  formatDurationFromMinutes,
  toApiAbsoluteUrl,
  resolveSupplementQuantity,
} from '@cfixe-monorepo/shared';
import type {
  GiftCardPublicConfigResponseObject,
  GiftCardCheckoutRequestObject,
  GiftCardCartSummaryRequestObject,
  GiftCardCartSummaryResponseObject,
  CatalogSelectionResponseObject,
  PublicPriceOptionObject,
  SupplementQuantity,
} from '@cfixe-monorepo/shared';
import { FormField } from '../../booking/components/shared/FormField';
import { CustomerInfoFields } from '../../booking/components/shared/CustomerInfoFields';
import { PhoneInput } from '../../booking/components/shared/PhoneInput';
import { PrestationConfigurator } from '../../booking/components/shared/PrestationConfigurator';
import { type SupplementPickerItem } from '../../booking/components/shared/SupplementPicker';
import { CategoryGroup } from '../../booking/components/shared/CategoryGroup';
import { SearchInput } from '../../booking/components/shared/SearchInput';
import { PaymentReassurance } from '../../booking/components/shared/PaymentReassurance';
import { groupItemsByCategory } from '../../booking/utils/category-grouping';
import { DEFAULT_LABELS } from '../../booking/types';
import { toSupplementSelectionPayload } from '../../booking/utils/supplement-selection';
import '../../booking/styles/booking-tunnel.scss';
import { isBuilderCutError } from '../../website/functions/builder-events';
import { GiftCardConfirmation } from './GiftCardConfirmation';
import { GiftCardUnavailable } from './GiftCardUnavailable';
import { GiftCardProgress } from './GiftCardProgress';
import {
  TunnelSummary,
  type TunnelSummaryCta,
  type TunnelSummaryItem,
  type TunnelSummaryProps,
  type TunnelSummarySupplement,
} from '../../booking/components/shared/TunnelSummary';
import { TunnelFooterBar } from '../../booking/components/shared/TunnelFooterBar';
import { useStepTransitionFocus } from '../../booking/components/shared/useStepTransitionFocus';
import { useScrollToError } from '../../booking/components/shared/useScrollToError';
import { ImageGallery } from '../../booking/components/shared/ImageGallery';
import { RedirectPaymentNotice } from '../../booking/components/shared/RedirectPaymentNotice';
import { REDIRECT_RETURN_PARAM, type RedirectReturnParam } from '../../pro-payment/redirect-return';
import { redirectReturnMessage, useRedirectReturn } from '../../pro-payment/useRedirectReturn';
import { GiftCardPaymentSection } from './GiftCardPaymentSection';
import type { GiftCardCallbacks } from '../api/create-gift-card-callbacks';

const TRANSLATIONS = MAPPED_TRANSLATION['fr-FR'] as unknown as Record<string, Record<string, string>>;
const T = TRANSLATIONS['GIFT_CARD_PUBLIC_PAGE'] ?? {};
const t = (key: string): string => T[key] ?? key;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type PurchaseType = 'AMOUNT' | 'PACK' | 'PRESTATION';
// Parcours en 2 étapes : sélection → coordonnées + cadeau (puis paiement inline dans la même étape).
type Step = 'selection' | 'details';
const STEPS: Step[] = ['selection', 'details'];

/** Sélection prestation confirmée (snapshot envoyé au checkout / preview). */
type ConfirmedPrestation = {
  prestation_id: number;
  quantity: number;
  selected_duration_minutes?: number;
  selected_supplement_ids?: number[];
  /** Quantités choisies au stepper pour des suppléments retenus (absent : une par prestation du bon). */
  supplement_quantities?: SupplementQuantity[];
};

const toVoucherType = (type: PurchaseType): VoucherType =>
  type === 'AMOUNT' ? VoucherType.AMOUNT : type === 'PACK' ? VoucherType.PACK : VoucherType.PRESTATION;

/** Aplatit les prix imbriqués (price_infos) du config vers la forme plate attendue par DurationSelector. */
function flattenPrices(p: CatalogSelectionResponseObject): PublicPriceOptionObject[] {
  return (p.prices ?? []).map(
    (pr): PublicPriceOptionObject => ({
      duration_minutes: pr.duration_minutes ?? 0,
      price: pr.price_infos?.price ?? 0,
      price_type: pr.price_infos?.type ?? 'FIXED',
      ...(pr.price_infos?.price_string ? { price_string: pr.price_infos.price_string } : {}),
    }),
  );
}

export interface PurchaseTunnelProps {
  slug: string;
  callbacks: GiftCardCallbacks;
  /**
   * Appelé quand l'achat est confirmé (bon activé). Utilisé par le widget embarqué pour émettre
   * l'event `propelsia:gift-card:completed`. La page hébergée peut l'ignorer.
   */
  onCompleted?: (voucherUuid: string) => void;
  /**
   * Locale BCP-47 (ex. `fr-FR`, `en-US`) pour le formatage des montants/téléphone côté client.
   * Les libellés UI restent en français (traductions fr-FR uniquement). Défaut : `fr-FR`.
   */
  locale?: string;
  /**
   * Présélection à l'ouverture (deep-link depuis la page prestation) : type d'achat + élément ciblé
   * (pack ou prestation). Le type prime sur le défaut calculé depuis la config.
   */
  preselect?: { type?: PurchaseType; packId?: number; prestationId?: number; durationMinutes?: number };
  /**
   * `pack` = achat direct d'un forfait : masque le sélecteur de type ET le cadre cadeau
   * (option « offrir » / destinataire) — le bon est acheté pour soi. Défaut : `full` (tunnel complet).
   */
  scope?: 'full' | 'pack';
  /**
   * Paramètre de retour Stripe de CE tunnel. Une modale passe le sien pour ne pas reprendre le retour
   * d'un tunnel intégré à la même page (et inversement). Défaut : `REDIRECT_RETURN_PARAM.GIFT_CARD`.
   */
  redirectReturnParam?: RedirectReturnParam;
  /**
   * `false` = ce tunnel ne reprend pas le retour Stripe (un autre tunnel de la page le détient, ex. un
   * widget embarqué qui ne l'a pas réservé). Défaut `true`.
   */
  handleRedirectReturn?: boolean;
}

export function PurchaseTunnel({
  callbacks,
  onCompleted,
  locale = 'fr-FR',
  preselect,
  scope = 'full',
  redirectReturnParam = REDIRECT_RETURN_PARAM.GIFT_CARD,
  handleRedirectReturn = true,
}: PurchaseTunnelProps): React.JSX.Element {
  // Achat d'un forfait précis (deep-link ?pack= / présélection) : jamais de cadre cadeau (case
  // « c'est un cadeau » + destinataire), quel que soit le `scope` passé par l'appelant — le forfait
  // est acheté pour soi. Piloter le masquage par l'intention (packId présélectionné) plutôt que par
  // le seul prop `scope` garantit le comportement pour tous les points d'entrée (page, widget, futur).
  const isPackScope = scope === 'pack' || preselect?.packId != null;
  const [config, setConfig] = useState<GiftCardPublicConfigResponseObject | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState<Step>('selection');

  const [type, setType] = useState<PurchaseType | null>(null);
  const [amountEuros, setAmountEuros] = useState('');
  // Focus du champ montant : on affiche la valeur brute en édition, et le montant formaté (ex. « 25,00 € »)
  // une fois la saisie terminée (blur) — comme le PriceInput de l'espace pro.
  const [amountFocused, setAmountFocused] = useState(false);
  const [packId, setPackId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  // Recherche + filtre catégorie (prestations).
  const [prestationSearch, setPrestationSearch] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  // Carte prestation dépliée (en cours de config) + sélection confirmée via « Ajouter ».
  const [expandedPrestationId, setExpandedPrestationId] = useState<number | null>(null);
  // Plusieurs prestations possibles dans un même bon (le schéma/back gèrent déjà un tableau).
  const [confirmedPrestations, setConfirmedPrestations] = useState<ConfirmedPrestation[]>([]);
  // Retrait par le × du récap (proposé à l'étape sélection seulement, cf. `summaryLines`). Dernière ligne
  // retirée → étape sélection : l'étape coordonnées sans sélection n'aurait plus ni action ni retour (sidebar
  // absente, barre du bas masquée en layout wide). On ne réagit qu'à ce geste explicite — pas d'effet global
  // « sélection vide ⇒ étape sélection » : le retour de Stripe/Klarna rouvre l'étape coordonnées sans
  // sélection en mémoire et serait renvoyé à tort.
  const removeConfirmedPrestation = (index: number): void => {
    const remaining = confirmedPrestations.filter((_, i): boolean => i !== index);
    setConfirmedPrestations(remaining);
    if (remaining.length === 0) setStep('selection');
  };
  const [selectedPrice, setSelectedPrice] = useState<PublicPriceOptionObject | null>(null);
  const [prestationSupplements, setPrestationSupplements] = useState<SupplementPickerItem[]>([]);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [giftSendDate, setGiftSendDate] = useState('');

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Aperçu panier (mini-panier + total formaté serveur).
  const [summary, setSummary] = useState<GiftCardCartSummaryResponseObject | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // Une fois le checkout fait, on bascule sur le paiement inline (uuid du pro_payment).
  const [paymentUuid, setPaymentUuid] = useState<string | null>(null);
  // Paiement Stripe accepté → on affiche la confirmation inline (poll du bon + code/PDF), sans redirect.
  const [paid, setPaid] = useState(false);

  /* Retour de redirection Stripe (Klarna…) : la page a été rechargée, seule la saisie du pro_payment
     compte — on rouvre son paiement, et la confirmation s'affiche dès que le backend le voit réglé. */
  const redirectReturn = useRedirectReturn({
    param: redirectReturnParam,
    enabled: handleRedirectReturn,
    poll: callbacks.onPollPaymentStatus,
    onSettled: ({ uuid }): void => {
      setPaymentUuid(uuid);
      setPaid(true);
    },
    /* Commande expirée (ou annulée) pendant le passage chez Klarna : plus rien à payer, retour au choix. */
    onExpired: (): void => {
      setPaymentUuid(null);
      setStep('selection');
    },
  });
  const returnedPaymentUuid = redirectReturn.uuid;
  useEffect((): void => {
    if (!returnedPaymentUuid) return;
    setPaymentUuid(returnedPaymentUuid);
    setStep('details');
  }, [returnedPaymentUuid]);
  const redirectNotice = redirectReturn.phase ? (
    <RedirectPaymentNotice
      phase={redirectReturn.phase}
      message={redirectReturnMessage(redirectReturn.phase, {
        verifying: t('PAYMENT_VERIFYING'),
        unverified: t('PAYMENT_REDIRECT_UNVERIFIED'),
        failed: t('PAYMENT_REDIRECT_FAILED'),
        pending: t('PAYMENT_REDIRECT_PENDING'),
        expired: t('PAYMENT_REDIRECT_EXPIRED'),
      })}
    />
  ) : null;

  useEffect((): (() => void) => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const cfg = await callbacks.onLoadConfig();
        if (cancelled) return;
        setConfig(cfg);
        // Type initial : présélection (deep-link) prioritaire ; puis, en scope « pack » (page Packs
        // dédiée, sélecteur de type masqué), on force PACK — sinon le défaut « AMOUNT » afficherait le
        // choix du montant au lieu de la liste des packs. Sinon défaut selon la config.
        if (preselect?.type) setType(preselect.type);
        else if (isPackScope && cfg.allow_packs) setType('PACK');
        else if (cfg.allow_amount) setType('AMOUNT');
        else if (cfg.allow_packs) setType('PACK');
        else if (cfg.allow_prestations) setType('PRESTATION');
        // Élément présélectionné : si la sélection est complète, on confirme et on saute à l'étape
        // coordonnées (« nom / prénom… »), sans « Ajouter » / « Continuer ». Le bouton « retour »
        // de l'étape coordonnées reste dispo pour réajuster (variante, supplément…).
        if (preselect?.packId != null) {
          // Forfait inconnu de la config (`?pack=` périmé ou forgé) : on reste à l'étape sélection, liste
          // des forfaits affichée — sauter à l'étape coordonnées sans sélection donnerait une étape sans
          // action ni retour (même cul-de-sac que pour une prestation non réservable, ci-dessous).
          const packId = preselect.packId;
          if (cfg.packs.some((p): boolean => p.pack_id === packId)) {
            setPackId(packId);
            setStep('details');
          }
        } else if (preselect?.prestationId != null) {
          const p = cfg.prestations.find((pp): boolean => pp.prestation_id === preselect.prestationId);
          const prices = p ? flattenPrices(p).filter((pr): boolean => hasBookableDuration(pr.duration_minutes)) : [];
          // Variante ciblée = celle dont la durée correspond au choix fait sur la page prestation
          // (`preselect.durationMinutes`). À défaut de correspondance (ou sans durée transmise), on
          // retombe sur la première variante réservable.
          const targetPrice =
            (preselect.durationMinutes != null
              ? prices.find((pr): boolean => pr.duration_minutes === preselect.durationMinutes)
              : undefined) ?? prices[0];
          // On ne présélectionne QUE s'il existe une variante réservable (avec durée). Sinon on laisse
          // la liste affichée (type PRESTATION) : la prestation n'est pas configurable en bon, ouvrir
          // une carte vide (selectedPrice null) donnerait une étape morte → on évite ce cul-de-sac.
          if (p && targetPrice) {
            setConfirmedPrestations([
              {
                prestation_id: p.prestation_id,
                quantity: 1,
                ...(targetPrice.duration_minutes ? { selected_duration_minutes: targetPrice.duration_minutes } : {}),
              },
            ]);
            setStep('details');
          }
        }
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [callbacks, preselect?.type, preselect?.packId, preselect?.prestationId, preselect?.durationMinutes, isPackScope]);

  // Aperçu panier : recalculé (debounce) à chaque changement de sélection. Le total est formaté
  // serveur (base + Σ suppléments × quantité) — seule source fiable avec suppléments (règle prix).
  useEffect((): (() => void) => {
    let payload: GiftCardCartSummaryRequestObject | null = null;
    if (type === 'AMOUNT') {
      const cents = amountEuros ? parsePriceToMinorUnit(amountEuros, config?.currency ?? 'eur', locale) : null;
      if (cents && cents > 0) payload = { type: VoucherType.AMOUNT, amount: cents };
    } else if (type === 'PACK') {
      if (packId != null) payload = { type: VoucherType.PACK, pack_id: packId };
    } else if (type === 'PRESTATION') {
      if (confirmedPrestations.length > 0)
        payload = { type: VoucherType.PRESTATION, prestations: confirmedPrestations };
    }

    if (!payload) {
      setSummary(null);
      setSummaryLoading(false);
      return (): void => undefined;
    }

    let cancelled = false;
    setSummaryLoading(true);
    const handle = setTimeout((): void => {
      void callbacks
        .onPreview(payload as GiftCardCartSummaryRequestObject)
        .then((res): void => {
          if (!cancelled) setSummary(res);
        })
        .catch((): void => {
          if (!cancelled) setSummary(null);
        })
        .finally((): void => {
          if (!cancelled) setSummaryLoading(false);
        });
    }, 400);

    return (): void => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [type, amountEuros, packId, confirmedPrestations, callbacks, config?.currency, locale]);

  const availableTypes = useMemo((): { value: PurchaseType; label: string }[] => {
    if (!config) return [];
    const list: { value: PurchaseType; label: string }[] = [];
    if (config.allow_amount) list.push({ value: 'AMOUNT', label: t('TYPE_AMOUNT') });
    if (config.allow_packs) list.push({ value: 'PACK', label: t('TYPE_PACK') });
    if (config.allow_prestations) list.push({ value: 'PRESTATION', label: t('TYPE_PRESTATION') });
    return list;
  }, [config]);

  const rootRef = useRef<HTMLDivElement | null>(null);
  useStepTransitionFocus(rootRef, step);
  const errorRef = useScrollToError(error);

  if (loading) {
    return (
      <div className="bt-root">
        <div className="bt-loading">
          <div className="bt-spinner" />
        </div>
      </div>
    );
  }
  if (loadError || !config || availableTypes.length === 0) {
    return <GiftCardUnavailable professionalName={config?.professional_name} scope={scope} />;
  }

  // Confirmation inline (après paiement) : poll du bon → code + lien PDF. Pas de redirection : marche
  // dans la page hébergée comme dans un widget embarqué sur un site tiers.
  if (paymentUuid && paid) {
    return (
      <div className="bt-root">
        <GiftCardConfirmation paymentUuid={paymentUuid} onReady={onCompleted} />
      </div>
    );
  }

  if (redirectReturn.isBlocking) {
    return (
      <div className="bt-root">
        <div className="bt-redirect-finalizing-view">{redirectNotice}</div>
      </div>
    );
  }

  // Formatage/parsing montant selon la devise du pro (décimales, symbole) via les helpers partagés.
  // La locale vient de la prop (défaut fr-FR) — comme le tunnel de réservation.
  const currency = config.currency || 'eur';
  const fmt = (minor: number): string => formatPriceFromMinorUnit(minor, currency, locale);
  const currencySymbol = getCurrencySymbol(currency, locale);

  // Montants prédéfinis (filtrés sur la plage autorisée) + montant actuellement sélectionné.
  const PRESET_AMOUNTS_CENTS = [2500, 5000, 7500, 10000, 15000, 20000];
  const presetAmounts = PRESET_AMOUNTS_CENTS.filter(
    (c): boolean =>
      (config.amount_min == null || c >= config.amount_min) && (config.amount_max == null || c <= config.amount_max),
  );
  const selectedAmountCents = amountEuros ? parsePriceToMinorUnit(amountEuros, currency, locale) : null;

  // Valeur affichée dans le champ montant : brute pendant l'édition, formatée (« 25,00 € ») hors focus.
  const amountInputValue =
    !amountFocused && selectedAmountCents != null && selectedAmountCents > 0 ? fmt(selectedAmountCents) : amountEuros;
  // Fin de saisie : on normalise la valeur brute selon les décimales de la devise (« 25,5 » → « 25.50 »).
  const handleAmountBlur = (): void => {
    setAmountFocused(false);
    if (selectedAmountCents != null && selectedAmountCents > 0) {
      setAmountEuros(formatMinorUnitToRawPrice(selectedAmountCents, currency, locale));
    }
  };

  const amountRangeHint = ((): string | null => {
    if (type !== 'AMOUNT') return null;
    const min = config.amount_min != null ? fmt(config.amount_min) : null;
    const max = config.amount_max != null ? fmt(config.amount_max) : null;
    if (min && max) return `${t('AMOUNT_RANGE')} : ${min} – ${max}`;
    if (min) return `${t('AMOUNT_RANGE')} : ≥ ${min}`;
    if (max) return `${t('AMOUNT_RANGE')} : ≤ ${max}`;
    return null;
  })();

  // Sélection valide ? (active le bouton « Continuer » de l'étape 1).
  const isSelectionValid = ((): boolean => {
    if (type === 'AMOUNT') {
      const cents = selectedAmountCents ?? 0;
      return (
        cents > 0 &&
        (config.amount_min == null || cents >= config.amount_min) &&
        (config.amount_max == null || cents <= config.amount_max)
      );
    }
    if (type === 'PACK') return packId != null;
    if (type === 'PRESTATION') return confirmedPrestations.length > 0;
    return false;
  })();

  // Lignes du mini-panier : composées depuis la sélection (libellés/contenu du config) + les prix
  // formatés serveur (summary). On retombe sur les `price_string` du config tant que le preview charge.
  const summaryLines = ((): TunnelSummaryItem[] => {
    if (type === 'AMOUNT') {
      const cents = selectedAmountCents ?? 0;
      if (cents <= 0) return [];
      // Montant libre : la ligne ne porte pas de prix (le bloc Total affiche déjà summary.total_price),
      // sinon le même montant apparaîtrait deux fois dans le récap.
      return [{ label: t('AMOUNT_LINE_LABEL'), price: undefined, supplements: [] }];
    }
    if (type === 'PACK') {
      if (packId == null) return [];
      const pack = config.packs.find((p): boolean => p.pack_id === packId);
      if (!pack) return [];
      const sublines = (pack.prestations ?? []).map(
        (pp): string => `${pp.quantity > 1 ? `${pp.quantity} × ` : ''}${pp.name}`,
      );
      return [
        {
          label: pack.name,
          price: summary?.items[0]?.line_price ?? pack.price_string,
          sublines,
          supplements: [],
        },
      ];
    }
    if (type === 'PRESTATION') {
      if (confirmedPrestations.length === 0) return [];
      // 1 ligne (retirable) par prestation ajoutée ; le prix de chaque ligne vient du preview serveur
      // dans le même ordre (le back parcourt `prestations` en ordre), sinon repli sur le prix config.
      return confirmedPrestations.map((cp, idx): TunnelSummaryItem => {
        const prestation = config.prestations.find((p): boolean => p.prestation_id === cp.prestation_id);
        const item = summary?.items[idx];
        const fallbackLabel = prestation
          ? cp.quantity > 1
            ? `${prestation.name} x${cp.quantity}`
            : prestation.name
          : '';
        const fallbackPrice = prestation ? flattenPrices(prestation)[0]?.price_string : undefined;
        // Durée choisie (par unité), affichée à côté du nom comme sur une fiche prestation — le cumul reste
        // dans le bloc Total. `formatDurationFromMinutes` : util partagé, identique au tunnel de booking.
        const baseLabel = item?.label ?? fallbackLabel;
        const durationStr =
          cp.selected_duration_minutes != null && hasBookableDuration(cp.selected_duration_minutes)
            ? formatDurationFromMinutes(cp.selected_duration_minutes)
            : '';
        // Suppléments : on privilégie le preview serveur (prix facturé exact) ; tant qu'il charge, on affiche
        // instantanément ceux choisis localement (nom + price_string du config) pour un rendu « détails » immédiat.
        const supplements =
          item?.supplements != null
            ? item.supplements.map(
                (s): TunnelSummarySupplement => ({ name: s.name, price: s.price, quantity: s.quantity }),
              )
            : (cp.selected_supplement_ids ?? [])
                .map((sid): TunnelSummarySupplement | null => {
                  const sup = prestation?.supplements?.find((s): boolean => s.supplement_id === sid);
                  // On ne garde que les suppléments facturés (prix > 0), comme le fait le preview serveur
                  // (voucher.public.service : `if (supplement?.price)`) → pas de scintillement d'un supplément
                  // gratuit affiché en local puis retiré à l'arrivée du preview.
                  if (!sup || !sup.price) return null;
                  // Au-delà d'un exemplaire, le prix unitaire du config ne serait pas le montant de la
                  // ligne (calculé serveur) : on n'affiche que la quantité en attendant le preview.
                  const supQuantity = resolveSupplementQuantity(cp, sid);
                  return supQuantity > 1
                    ? { name: sup.name, quantity: supQuantity }
                    : { name: sup.name, price: sup.price_string };
                })
                .filter((s): s is TunnelSummarySupplement => s !== null);
        return {
          label: durationStr ? `${baseLabel} · ${durationStr}` : baseLabel,
          price: item?.line_price ?? fallbackPrice,
          supplements,
          // × de retrait à l'étape sélection seulement, comme le tunnel de réservation (étape prestations) :
          // plus loin, « Retour » ramène à la sélection pour réajuster.
          ...(step === 'selection' ? { onRemove: (): void => removeConfirmedPrestation(idx) } : {}),
        };
      });
    }
    return [];
  })();

  // Ouvre une prestation pour configuration (init 1ère variante de prix + suppléments décochés).
  const openPrestation = (p: CatalogSelectionResponseObject): void => {
    // Seules les lignes RÉSERVABLES (avec durée) peuvent faire l'objet d'un bon : une ligne
    // « vitrine / sur devis » (sans durée) n'est ni proposée ni sélectionnable.
    const prices = flattenPrices(p).filter((pr): boolean => hasBookableDuration(pr.duration_minutes));
    setExpandedPrestationId(p.prestation_id);
    setSelectedPrice(prices[0] ?? null);
    setPrestationSupplements((p.supplements ?? []).map((s): SupplementPickerItem => ({ ...s, selected: false })));
    setQuantity(1);
  };

  // « Ajouter » : empile la prestation configurée (snapshot pour le checkout) et replie la carte.
  // On peut en ajouter autant qu'on veut dans le même bon (y compris la même plusieurs fois).
  const confirmPrestation = (): void => {
    if (expandedPrestationId == null) return;
    const lineQuantity = Math.max(1, quantity);
    // Quantités : seulement celles touchées au stepper ; les autres suivent la quantité du bon.
    const { selected_supplement_ids: supplementIds, supplement_quantities: supplementQuantities } =
      toSupplementSelectionPayload({ supplements: prestationSupplements, quantity: lineQuantity });
    setConfirmedPrestations((arr): ConfirmedPrestation[] => [
      ...arr,
      {
        prestation_id: expandedPrestationId,
        quantity: lineQuantity,
        ...(selectedPrice?.duration_minutes ? { selected_duration_minutes: selectedPrice.duration_minutes } : {}),
        ...(supplementIds.length > 0 ? { selected_supplement_ids: supplementIds } : {}),
        ...(supplementQuantities ? { supplement_quantities: supplementQuantities } : {}),
      },
    ]);
    setExpandedPrestationId(null);
  };

  const goToDetails = (): void => {
    setError(null);
    if (isSelectionValid) setStep('details');
  };

  const back = (): void => {
    setError(null);
    setStep('selection');
  };

  const handleCheckout = async (): Promise<void> => {
    setError(null);

    if (!firstname.trim() || !lastname.trim() || !EMAIL_RE.test(email.trim())) {
      setError(t('INVALID_EMAIL'));
      return;
    }
    if (isGift && !EMAIL_RE.test(recipientEmail.trim())) {
      setError(t('INVALID_RECIPIENT_EMAIL'));
      return;
    }
    if (type === 'PRESTATION' && confirmedPrestations.length === 0) {
      setError(t('SELECT_PRESTATION'));
      return;
    }

    const payload: GiftCardCheckoutRequestObject = {
      buyer: {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: email.trim(),
        ...(phone.trim() ? { phone_number: phone.trim() } : {}),
      },
      type: toVoucherType(type ?? 'AMOUNT'),
      is_gift: isGift,
      ...(type === 'AMOUNT' ? { amount: parsePriceToMinorUnit(amountEuros, currency, locale) ?? 0 } : {}),
      ...(type === 'PACK' && packId != null ? { pack_id: packId } : {}),
      ...(type === 'PRESTATION' && confirmedPrestations.length > 0 ? { prestations: confirmedPrestations } : {}),
      ...(isGift
        ? {
            ...(recipientName.trim() ? { gift_recipient_name: recipientName.trim() } : {}),
            gift_recipient_email: recipientEmail.trim(),
            ...(recipientPhone.trim() ? { gift_recipient_phone: recipientPhone.trim() } : {}),
            ...(giftMessage.trim() ? { gift_message: giftMessage.trim() } : {}),
            ...(giftSendDate ? { gift_send_date: new Date(giftSendDate).toISOString() } : {}),
          }
        : {}),
    };

    setSubmitting(true);
    try {
      const res = await callbacks.onCheckout(payload);
      // Sans uuid de paiement, impossible de monter GiftCardPaymentSection : on traite comme une erreur
      // au lieu de laisser le bouton bloqué en chargement indéfiniment.
      if (!res.payment_uuid) {
        setError(t('ERROR_GENERIC'));
        return;
      }
      // Paiement inline : on monte GiftCardPaymentSection (au lieu de rediriger vers /pro-payment).
      setPaymentUuid(res.payment_uuid);
    } catch (e) {
      // Cut builder (aperçu de l'éditeur) : message informatif affiché tel quel — sinon générique.
      setError(isBuilderCutError(e) ? e.message : t('ERROR_GENERIC'));
    } finally {
      // Garantit la sortie de l'état chargement dans tous les chemins. Au succès (uuid valide), on bascule
      // sur le paiement inline donc `submitting` n'a plus d'effet visible — mais on le réinitialise quand même.
      setSubmitting(false);
    }
  };

  const currentIndex = STEPS.indexOf(step);
  // Le formulaire Stripe (avec sa propre réassurance) n'est visible qu'après checkout, sur l'étape
  // « details ». Partout ailleurs, on affiche la barre de réassurance inline.
  const paymentFormVisible = step === 'details' && paymentUuid != null;
  // Masquée à l'étape « selection » : la page doit d'abord présenter l'offre, pas les logos de paiement.
  const showTrustBar =
    step !== 'selection' &&
    !paymentFormVisible &&
    config.allowed_payment_methods != null &&
    config.allowed_payment_methods.length > 0;

  // Validité du bon : affichée à chaque étape (note discrète en sélection, encadré mis en évidence
  // au moment de l'achat). `config` est garanti non-null ici (early-returns plus haut).
  // Un forfait porte SA PROPRE validité (`pack.validity_months`) — distincte de la validité par défaut
  // des cartes cadeaux (`default_validity_months`). Quand un forfait est sélectionné, on affiche donc
  // la sienne ; sinon (montant / prestation) on retombe sur la validité par défaut.
  const selectedPack =
    type === 'PACK' && packId != null ? config.packs.find((p): boolean => p.pack_id === packId) : undefined;
  const validityMonths = selectedPack ? selectedPack.validity_months : config.default_validity_months;
  const validityText = validityMonths > 0 ? t('VALIDITY_NOTICE').replace('{months}', String(validityMonths)) : null;

  /* ── Étape 1 : sélection ─────────────────────────────────────────────── */
  const selectionStep = (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">
          {isPackScope ? t('STEP_SELECTION_TITLE_PACK') : t('STEP_SELECTION_TITLE')}
        </h2>
        <p className="bt-step-header__subtitle">{config.professional_name}</p>
        {validityText && <p className="gc-validity-note">📅 {validityText}</p>}
      </div>

      <div className="bt-checkout">
        <div className="bt-checkout__section">
          {!isPackScope && availableTypes.length > 1 && (
            <div className="bt-duration">
              <span className="bt-duration__label">{t('CHOOSE_TYPE')}</span>
              <div className="bt-duration__options">
                {availableTypes.map(
                  (opt): React.JSX.Element => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`bt-duration__option ${type === opt.value ? 'bt-duration__option--selected' : ''}`}
                      onClick={(): void => setType(opt.value)}
                    >
                      <span className="bt-duration__option-duration">{opt.label}</span>
                    </button>
                  ),
                )}
              </div>
            </div>
          )}

          {type === 'AMOUNT' && (
            <div className="bt-duration">
              <span className="bt-duration__label">{t('AMOUNT_LABEL')}</span>
              {presetAmounts.length > 0 && (
                <div className="bt-duration__options">
                  {presetAmounts.map(
                    (cents): React.JSX.Element => (
                      <button
                        key={cents}
                        type="button"
                        className={`bt-duration__option ${selectedAmountCents === cents ? 'bt-duration__option--selected' : ''}`}
                        onClick={(): void => setAmountEuros(formatMinorUnitToRawPrice(cents, currency, locale))}
                      >
                        <span className="bt-duration__option-duration">{fmt(cents)}</span>
                      </button>
                    ),
                  )}
                </div>
              )}
              <div className="bt-field">
                <label className="bt-field__label" htmlFor="gc-amount">
                  {`${t('AMOUNT_CUSTOM')} (${currencySymbol})`}
                </label>
                <input
                  id="gc-amount"
                  type="text"
                  inputMode="decimal"
                  className="bt-field__input"
                  value={amountInputValue}
                  onChange={(e): void => setAmountEuros(e.target.value.replace(',', '.'))}
                  onFocus={(): void => setAmountFocused(true)}
                  onBlur={handleAmountBlur}
                  placeholder={amountRangeHint ?? undefined}
                />
              </div>
            </div>
          )}

          {type === 'PACK' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span className="bt-duration__label">{t('SELECT_PACK')}</span>
              {config.packs.map((p): React.JSX.Element => {
                return (
                  <div
                    key={p.pack_id}
                    className={`bt-service-card ${packId === p.pack_id ? 'bt-service-card--expanded' : ''}`}
                  >
                    <div className="bt-service-card__header" onClick={(): void => setPackId(p.pack_id)}>
                      <ImageGallery
                        pictures={p.pictures ?? []}
                        alt={p.name}
                        labels={{ close: t('CLOSE'), previous: t('PHOTO_PREVIOUS'), next: t('PHOTO_NEXT') }}
                      />
                      <div className="bt-service-card__info">
                        <p className="bt-service-card__name">{p.name}</p>
                        {p.price_string && (
                          <div className="bt-service-card__meta">
                            <span className="bt-service-card__price">{p.price_string}</span>
                          </div>
                        )}
                        {p.description && (
                          <p className="bt-service-card__description" style={{ whiteSpace: 'pre-line' }}>
                            {p.description}
                          </p>
                        )}
                        {/* `?? []` : le back a déjà sérialisé ce tableau vide en `null`
                            (transformToSchema) alors que le type le dit non-nullable —
                            un pack sans prestation faisait planter tout le tunnel ici. */}
                        {(p.prestations ?? []).length > 0 && (
                          <ul
                            style={{
                              margin: '6px 0 0',
                              paddingLeft: 16,
                              fontSize: 13,
                              color: 'var(--bt-muted, #888)',
                            }}
                          >
                            {(p.prestations ?? []).map(
                              (pp, i): React.JSX.Element => (
                                <li key={`${pp.prestation_id}-${i}`}>
                                  {pp.quantity > 1 ? `${pp.quantity} × ` : ''}
                                  {pp.name}
                                </li>
                              ),
                            )}
                          </ul>
                        )}
                      </div>
                      <button type="button" className="bt-cta bt-cta--small">
                        {packId === p.pack_id ? t('SELECTED') : t('CHOOSE')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {type === 'PRESTATION' &&
            (() => {
              const renderPrestationCard = (p: CatalogSelectionResponseObject): React.JSX.Element => {
                const isOpen = expandedPrestationId === p.prestation_id;
                const addedCount = confirmedPrestations.filter(
                  (c): boolean => c.prestation_id === p.prestation_id,
                ).length;
                const prices = flattenPrices(p).filter((pr): boolean => hasBookableDuration(pr.duration_minutes));
                const priceStr = prices[0]?.price_string ?? (prices[0] ? fmt(prices[0].price) : undefined);
                return (
                  <div key={p.prestation_id} className={`bt-service-card ${isOpen ? 'bt-service-card--expanded' : ''}`}>
                    <div className="bt-service-card__header" onClick={(): void => openPrestation(p)}>
                      <div className="bt-service-card__info">
                        <p className="bt-service-card__name">{p.name}</p>
                        {priceStr && (
                          <div className="bt-service-card__meta">
                            <span className="bt-service-card__price">{priceStr}</span>
                          </div>
                        )}
                      </div>
                      {!isOpen && (
                        <>
                          {addedCount > 0 && <span className="bt-service-card__added">✓ {addedCount}</span>}
                          <button type="button" className="bt-cta bt-cta--small">
                            {t('CHOOSE')}
                          </button>
                        </>
                      )}
                    </div>
                    {isOpen && selectedPrice && (
                      <div className="bt-service-card__body">
                        <PrestationConfigurator
                          prices={prices}
                          selectedPrice={selectedPrice}
                          onPriceChange={setSelectedPrice}
                          supplements={prestationSupplements}
                          onSupplementsChange={setPrestationSupplements}
                          supplementDefaultQuantity={Math.max(1, quantity)}
                          quantity={quantity}
                          onQuantityChange={setQuantity}
                          formatPrice={fmt}
                          actionLabel={DEFAULT_LABELS.add_to_selection}
                          onAction={confirmPrestation}
                          labels={{
                            duration_label: DEFAULT_LABELS.duration_label,
                            // Jamais utilisé ici : le catalogue cadeau ne porte pas de drapeau de
                            // disponibilité, donc aucune formule n'est grisée.
                            duration_unavailable: DEFAULT_LABELS.duration_unavailable,
                            price_from: DEFAULT_LABELS.price_from,
                            on_quote: DEFAULT_LABELS.on_quote,
                            free: DEFAULT_LABELS.free,
                            supplements_label: DEFAULT_LABELS.supplements_label,
                            add_supplement: DEFAULT_LABELS.add_supplement,
                            remove_supplement: DEFAULT_LABELS.remove_supplement,
                            supplements_show_more_one: DEFAULT_LABELS.supplements_show_more_one,
                            supplements_show_more_other: DEFAULT_LABELS.supplements_show_more_other,
                            quantity_label: t('QUANTITY'),
                            decrease_quantity: DEFAULT_LABELS.decrease_quantity,
                            increase_quantity: DEFAULT_LABELS.increase_quantity,
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              };

              const query = prestationSearch.trim().toLowerCase();
              const filtered = config.prestations.filter(
                (p): boolean =>
                  (activeCategoryId == null || p.category_id === activeCategoryId) &&
                  (!query || p.name.toLowerCase().includes(query)),
              );
              const showSearch = config.prestations.length > 8;
              const grouped = activeCategoryId == null && !query && config.categories.length > 1;

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span className="bt-duration__label">{t('SELECT_PRESTATION')}</span>
                  {showSearch && (
                    <SearchInput
                      value={prestationSearch}
                      onChange={setPrestationSearch}
                      placeholder={DEFAULT_LABELS.search_placeholder}
                      clearLabel={DEFAULT_LABELS.clear_search}
                    />
                  )}
                  <CategoryGroup
                    categories={config.categories}
                    activeCategoryId={activeCategoryId}
                    onChange={setActiveCategoryId}
                    allCategoriesLabel={DEFAULT_LABELS.all_categories}
                  />
                  {grouped
                    ? groupItemsByCategory(
                        filtered,
                        config.categories,
                        (p): number | null | undefined => p.category_id,
                      ).map(
                        (group): React.JSX.Element => (
                          <Fragment key={`cat-${group.catalog_category_id ?? 'none'}`}>
                            {group.name && <div className="bt-service-group__title">{group.name}</div>}
                            {group.items.map(renderPrestationCard)}
                          </Fragment>
                        ),
                      )
                    : filtered.map(renderPrestationCard)}
                </div>
              );
            })()}
        </div>
      </div>
    </div>
  );

  /* ── Étape 2 : vos coordonnées + cadeau → paiement inline ────────────── */
  const detailsStep = (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">{paymentUuid ? t('STEP_PAYMENT_TITLE') : t('STEP_DETAILS_TITLE')}</h2>
        <p className="bt-step-header__subtitle">{config.professional_name}</p>
      </div>

      <div className="bt-checkout">
        {validityText && <div className="gc-validity-highlight">📅 {validityText}</div>}
        {paymentUuid && redirectNotice}
        {!paymentUuid ? (
          <>
            {/* Vos coordonnées (acheteur) */}
            <div className="bt-checkout__section">
              <h3 className="bt-checkout__section-title">{t('BUYER_SECTION')}</h3>
              <CustomerInfoFields
                idPrefix="gc"
                values={{ firstname, lastname, email, phone }}
                phoneRequired={false}
                locale={locale}
                labels={{
                  firstname: t('BUYER_FIRSTNAME'),
                  lastname: t('BUYER_LASTNAME'),
                  email: t('BUYER_EMAIL'),
                  phone: t('BUYER_PHONE'),
                }}
                onChange={(field, value): void => {
                  if (field === 'firstname') setFirstname(value);
                  else if (field === 'lastname') setLastname(value);
                  else if (field === 'email') setEmail(value);
                  else setPhone(value);
                }}
              />
            </div>

            {/* C'est un cadeau ? — masqué en scope « pack » (achat direct du forfait pour soi). */}
            {!isPackScope && (
              <div className="bt-checkout__section">
                <FormField
                  id="gc-is-gift"
                  label={t('IS_GIFT_QUESTION')}
                  type="checkbox"
                  value={isGift}
                  onChange={(v): void => setIsGift(Boolean(v))}
                />
                {isGift && (
                  <>
                    <FormField
                      id="gc-rec-name"
                      label={t('RECIPIENT_NAME')}
                      type="text"
                      value={recipientName}
                      onChange={(v): void => setRecipientName(String(v))}
                    />
                    <FormField
                      id="gc-rec-email"
                      label={t('RECIPIENT_EMAIL')}
                      type="text"
                      required
                      value={recipientEmail}
                      onChange={(v): void => setRecipientEmail(String(v))}
                    />
                    <div className="bt-field">
                      <label className="bt-field__label" htmlFor="gc-rec-phone">
                        {t('RECIPIENT_PHONE')}
                      </label>
                      <PhoneInput
                        id="gc-rec-phone"
                        value={recipientPhone}
                        onChange={setRecipientPhone}
                        locale={locale}
                        countryCodeLabel={DEFAULT_LABELS.phone_country_code}
                        placeholder={DEFAULT_LABELS.phone_placeholder}
                      />
                    </div>
                    <FormField
                      id="gc-msg"
                      label={t('GIFT_MESSAGE')}
                      type="textarea"
                      value={giftMessage}
                      onChange={(v): void => setGiftMessage(String(v))}
                    />
                    <FormField
                      id="gc-send-date"
                      label={t('GIFT_SEND_DATE')}
                      type="date"
                      value={giftSendDate}
                      onChange={(v): void => setGiftSendDate(String(v))}
                    />
                  </>
                )}
              </div>
            )}

            {error && (
              <p className="bt-error" role="alert" ref={errorRef}>
                {error}
              </p>
            )}
          </>
        ) : (
          <GiftCardPaymentSection
            uuid={paymentUuid}
            redirectReturnParam={redirectReturnParam}
            callbacks={callbacks}
            stripePublishableKey={public_env.STRIPE_PUBLISHABLE_KEY}
            allowedPaymentMethods={config.allowed_payment_methods}
            labels={{
              pay: t('PAY_NOW'),
              secure_payment: t('SECURE_PAYMENT'),
              payment_methods_accepted: t('PAYMENT_METHODS_ACCEPTED'),
              error_generic: t('ERROR_GENERIC'),
              finalizing: t('PAYMENT_FINALIZING'),
              verifying: t('PAYMENT_VERIFYING'),
              retry: t('RETRY'),
              terms_accept: t('TERMS_ACCEPT'),
              terms_read: t('TERMS_READ'),
            }}
            termsPdfUrl={
              config.terms_pdf_url ? toApiAbsoluteUrl(public_env.API_ENDPOINT, config.terms_pdf_url) : undefined
            }
            termsAccepted={termsAccepted}
            onTermsAcceptedChange={setTermsAccepted}
            onPaid={(): void => setPaid(true)}
          />
        )}
      </div>
    </div>
  );

  /* Récap : mêmes lignes pour la sidebar (layout wide) et la feuille de la barre du bas. */
  const summaryProps: Omit<TunnelSummaryProps, 'variant'> = {
    items: summaryLines,
    totalValue: summary?.total_price ?? (summaryLoading ? '…' : ''),
    totalDuration: summary?.total_duration,
    contextRows:
      isGift && recipientName.trim()
        ? [{ icon: '🎁', text: `${t('FOR_RECIPIENT')} ${recipientName.trim()}` }]
        : undefined,
    labels: { selection_title: t('SUMMARY_TITLE'), total_label: t('TOTAL'), remove: t('REMOVE') },
  };

  /* Actions de l'étape, rendues dans la sidebar (wide) ou la barre du bas (colonne unique). Paiement
     ouvert : la section Stripe porte son bouton, et le retour n'est plus proposé. */
  const primary = ((): TunnelSummaryCta | undefined => {
    if (step === 'selection') return { label: t('CONTINUE'), onClick: goToDetails, disabled: !isSelectionValid };
    if (paymentUuid) return undefined;
    return { label: t('GO_TO_PAYMENT'), onClick: (): void => void handleCheckout(), loading: submitting };
  })();
  const onBack = step === 'details' && !paymentUuid ? back : undefined;
  // Sidebar absente tant que rien n'est choisi, comme la réservation (l'action y serait grisée).
  const hasSelection = summaryLines.length > 0;

  return (
    <div className="bt-root" ref={rootRef}>
      <GiftCardProgress currentIndex={currentIndex} total={STEPS.length} />
      {/* Commande expirée pendant la redirection : le bloc paiement a disparu, le bandeau reste en tête. */}
      {redirectReturn.phase === 'expired' && redirectNotice}

      {showTrustBar && (
        <div className="bt-trust-bar">
          <PaymentReassurance
            layout="inline"
            methods={config.allowed_payment_methods}
            secureLabel={t('SECURE_PAYMENT')}
            methodsLabel={t('PAYMENT_METHODS_ACCEPTED')}
          />
        </div>
      )}

      <div className="bt-layout">
        <div className="bt-main">
          {step === 'selection' && selectionStep}
          {step === 'details' && detailsStep}
        </div>

        <TunnelSummary
          {...summaryProps}
          variant="sidebar"
          cta={hasSelection ? primary : undefined}
          backAction={hasSelection && onBack ? { label: t('BACK'), onClick: onBack } : undefined}
        />
      </div>

      <TunnelFooterBar
        onBack={onBack}
        primary={primary}
        totalValue={summaryProps.totalValue}
        count={summaryLines.length}
        sheetTitle={t('SUMMARY_TITLE')}
        sheet={<TunnelSummary {...summaryProps} variant="sheet" />}
        stepKey={paymentUuid ? 'payment' : step}
        labels={{
          back: t('BACK'),
          close: t('CLOSE'),
          show_detail: t('SHOW_DETAIL'),
          hide_detail: t('HIDE_DETAIL'),
          item_singular: t('ITEM_SINGULAR'),
          item_plural: t('ITEM_PLURAL'),
        }}
      />
    </div>
  );
}
